import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import { createCommonStyles } from "../theme/styles";
import {
  colors,
  getActualTableRowBackground,
  getTableHeaderStyles,
} from "../theme/colors";
import api from "../services/api";
import { isSuperAdmin } from "../utils/userHelper";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("sr-RS");
};

const CoopRequests = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const { isDarkMode } = useAppTheme();
  const commonStyles = createCommonStyles(isDarkMode);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const response = await api.get(
          `/contact/demo-requests?page=${page + 1}&limit=${rowsPerPage}`
        );
        const data =
          response?.requests ||
          response?.data?.demoRequests ||
          response?.data ||
          response ||
          [];
        const pagination =
          response?.pagination || response?.data?.pagination || null;
        setRequests(Array.isArray(data) ? data : []);
        setTotal(pagination?.total ?? (Array.isArray(data) ? data.length : 0));
      } catch (err) {
        setError(err.message || "Neuspešno učitavanje zahteva.");
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [page, rowsPerPage]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const next = parseInt(event.target.value, 10);
    setRowsPerPage(next);
    setPage(0);
  };

  if (!currentUser || !isSuperAdmin(currentUser)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2, ...commonStyles.alert("error") }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          sx={{
            backgroundColor: isDarkMode
              ? "transparent"
              : commonStyles.tableContainer.backgroundColor,
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: colors.table.header.background,
                  "& .MuiTableCell-head": {
                    color: commonStyles.textPrimary.color,
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                    fontWeight: 600,
                  },
                }}
              >
                <TableCell>Ime</TableCell>
                <TableCell>Prezime</TableCell>
                <TableCell>Radnja / firma</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Telefon</TableCell>
                <TableCell>Poruka</TableCell>
                <TableCell>Datum</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    Trenutno nema zahteva.
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request, index) => (
                  <TableRow
                    key={request._id || `${request.email}-${index}`}
                    sx={{
                      backgroundColor: getActualTableRowBackground(
                        index,
                        isDarkMode,
                      ),
                      "&:hover": {
                        background: isDarkMode
                          ? "rgba(75, 85, 99, 0.7)"
                          : "#f5f5f5",
                      },
                      "& .MuiTableCell-body": {
                        borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
                        color: commonStyles.textPrimary.color,
                      },
                    }}
                  >
                    <TableCell sx={commonStyles.textPrimary}>
                      {request.firstName || "-"}
                    </TableCell>
                    <TableCell sx={commonStyles.textPrimary}>
                      {request.lastName || "-"}
                    </TableCell>
                    <TableCell sx={commonStyles.textPrimary}>
                      {request.businessName || "-"}
                    </TableCell>
                    <TableCell sx={commonStyles.textPrimary}>
                      {request.email || "-"}
                    </TableCell>
                    <TableCell sx={commonStyles.textPrimary}>
                      {request.phone || "-"}
                    </TableCell>
                    <TableCell sx={commonStyles.textPrimary}>
                      {request.message || "-"}
                    </TableCell>
                    <TableCell sx={commonStyles.textPrimary}>
                      {formatDate(request.createdAt || request.date)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              color: commonStyles.textPrimary.color,
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                color: commonStyles.textSecondary.color,
              },
            }}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default CoopRequests;
